import { versionedNamespace } from './versionedNamespace';

export const NAMESPACE = 'wds';

type ClassValue = string | false | null | undefined;
type VariantMap = Record<string, readonly string[]>;

type Config<
  Slots extends readonly string[],
  Variants extends VariantMap,
  States extends readonly string[],
> = {
  slots?: Slots;
  variants?: Variants;
  states?: States;
};

type Props<Variants extends VariantMap, States extends readonly string[]> = {
  [V in keyof Variants]?: Variants[V][number] | false | null;
} & {
  [S in States[number]]?: boolean | null;
};

const kebab = (value: string) =>
  value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

const join = (...classes: ClassValue[]) => classes.filter(Boolean).join(' ');

export const createClassNames = <
  const Slots extends readonly string[] = [],
  const Variants extends VariantMap = Record<never, never>,
  const States extends readonly string[] = [],
>(
  blockName: string,
  config: Config<Slots, Variants, States> = {},
  versionedNs: string = versionedNamespace,
) => {
  const namespaces = versionedNs ? [NAMESPACE, versionedNs] : [NAMESPACE];

  const block = `${NAMESPACE}-${blockName}`;
  const slot = (name: Slots[number]) => `${block}__${kebab(name)}`;
  const variant = <V extends keyof Variants>(
    name: V,
    value: Variants[V][number],
  ) => `${block}--${kebab(String(name))}-${kebab(value)}`;
  const state = (name: States[number]) => `${block}--${kebab(name)}`;

  const blockClasses = (props: Props<Variants, States>) => {
    const variants = Object.entries(config.variants ?? {})
      .map(([name, values]) => {
        const value = props[name as keyof typeof props] as string | undefined;
        if (!value || !values.includes(value)) return null;
        return `--${kebab(name)}-${kebab(value)}`;
      })
      .filter(Boolean) as string[];

    const states = (config.states ?? [])
      .filter(name => props[name as keyof typeof props])
      .map(name => `--${kebab(name)}`);

    const tails = ['', ...variants, ...states];

    return namespaces.flatMap(ns =>
      tails.map(tail => `${ns}-${blockName}${tail}`),
    );
  };

  const root = (
    props: Props<Variants, States> = {},
    ...classes: ClassValue[]
  ) => join(...blockClasses(props), ...classes);

  const element = (name: Slots[number], ...classes: ClassValue[]) =>
    join(
      ...namespaces.map(ns => `${ns}-${blockName}__${kebab(name)}`),
      ...classes,
    );

  return { root, element, slot, variant, state, block } as const;
};

export const defineClassNames = <
  const Slots extends readonly string[] = [],
  const Variants extends VariantMap = Record<never, never>,
  const States extends readonly string[] = [],
>(
  blockName: string,
  config: Config<Slots, Variants, States> = {},
) => createClassNames(blockName, config);
